import {
  Component,
  OnInit,
  Injector,
  OnDestroy,
  Renderer2,
  ElementRef,
} from "@angular/core";
import { Router, RouterLinkActive, RouterLink } from "@angular/router";

import { MenuService } from "../../core/menu/menu.service";
import { SettingsService } from "../../core/settings/settings.service";
import { UserblockComponent } from "./userblock/userblock.component";
import { NgFor, NgClass, NgIf, AsyncPipe } from "@angular/common";
import { MatRipple } from "@angular/material/core";
import { TourAnchorMatMenuDirective } from "ngx-ui-tour-md-menu";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { TourAnchorClickInterceptorDirective } from "src/app/shared/directives/tour-anchor-click/tour-anchor-click.directive";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
  imports: [
    UserblockComponent,
    NgFor,
    NgClass,
    RouterLinkActive,
    NgIf,
    MatRipple,
    RouterLink,
    TourAnchorClickInterceptorDirective,
    TourAnchorMatMenuDirective,
    TranslatePipe,
    AsyncPipe,
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  menuItems: any[];
  router: Router;
  menuItems2;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  private documentClickListener: Function;

  constructor(
    public menu: MenuService,
    public settings: SettingsService,
    public injector: Injector,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private translate: TranslateService,
  ) {
    this.menuItems = menu.getMenu();
    this.menuItems2 = menu.menuItems;
  }

  ngOnInit() {
    this.router = this.injector.get(Router);

    this.router.events.subscribe(() => {
      this.removeFloatingNav();
      window.scrollTo(0, 0);
    });

    this.documentClickListener = this.renderer.listen(
      "document",
      "click",
      (event: Event) => {
        if (
          window.innerWidth < 768 &&
          this.settings.getLayoutSetting("asideToggled") &&
          !this.elementRef.nativeElement.contains(event.target)
        ) {
          this.settings.setLayoutSetting("asideToggled", false);
          this.settings.setLayoutSetting("isCollapsed", true);
        }
      },
    );
  }

  ngOnDestroy() {
    if (this.documentClickListener) {
      this.documentClickListener();
    }
  }

  toggleSubmenuClick(event: Event) {
    event.preventDefault();

    // In collapsed mode: expand sidebar then open the submenu
    if (this.isSidebarCollapsed() || this.isSidebarCollapsedText()) {
      this.settings.setLayoutSetting("isCollapsed", false);
      // Wait for sidebar expand transition then open the submenu
      const target2 = event.currentTarget as HTMLElement;
      const parentLi2 = target2.closest("li");
      const ul2 = parentLi2?.querySelector(
        ":scope > ul.sidebar-subnav",
      ) as HTMLElement | null;
      if (ul2) {
        setTimeout(() => {
          this.elementRef.nativeElement
            .querySelectorAll(".sidebar-subnav")
            .forEach((el: HTMLElement) => {
              if (el !== ul2) this.closeMenu(el);
            });
          this.openMenu(ul2);
        }, 620);
      }
      return;
    }

    const target = event.currentTarget as HTMLElement;
    // When the tour is active, the click target can be the tour wrapper (e.g. tour-anchor-opener)
    // instead of the <a>, so nextElementSibling would point to the wrong element. Find the
    // submenu ul via the parent <li> so it works with or without the tour.
    const parentLi = target.closest("li");
    const ul = parentLi?.querySelector(
      ":scope > ul.sidebar-subnav",
    ) as HTMLElement | null;

    if (!ul) return;

    // Hide other submenus
    const parentNav = ul.closest(".sidebar-subnav");
    this.elementRef.nativeElement
      .querySelectorAll(".sidebar-subnav")
      .forEach((el: HTMLElement) => {
        if (el !== parentNav && el !== ul) {
          this.closeMenu(el);
        }
      });

    // Close child submenus
    ul.querySelectorAll(".sidebar-subnav").forEach((el: HTMLElement) => {
      this.closeMenu(el);
    });

    // Toggle current submenu
    const isOpen = ul.style.height === "auto" || parseInt(ul.style.height) > 0;
    if (isOpen) {
      this.closeMenu(ul);
    } else {
      this.openMenu(ul);
    }
  }

  private openMenu(element: HTMLElement) {
    const scrollHeight = element.scrollHeight;
    this.renderer.setStyle(element, "height", `${scrollHeight}px`);
    this.renderer.addClass(element, "opening");

    setTimeout(() => {
      this.renderer.setStyle(element, "height", "auto");
      this.renderer.removeClass(element, "opening");
    }, 300); // Adjust timing based on your transition duration
  }

  private closeMenu(element: HTMLElement) {
    const scrollHeight = element.scrollHeight;
    this.renderer.setStyle(element, "height", `${scrollHeight}px`);

    // Force reflow

    element.offsetHeight;

    this.renderer.setStyle(element, "height", "0");
    this.renderer.removeClass(element, "opening");
  }

  toggleSubmenuHover(_event: Event) {
    this.removeFloatingNav();
  }

  private calculateFloatingNavPosition(anchor: HTMLElement, nav: HTMLElement) {
    const vwHeight = document.body.clientHeight;
    const anchorRect = anchor.getBoundingClientRect();
    const navHeight = nav.offsetHeight;
    const safeOffsetValue = 40 * 5;
    const safeOffset = Math.min(navHeight, safeOffsetValue);
    const displacement = 25;

    const top =
      vwHeight - anchorRect.top > safeOffset
        ? anchorRect.top
        : vwHeight - safeOffset - displacement;

    return {
      top,
      position: this.settings.getLayoutSetting("isFixed") ? "fixed" : "absolute",
    };
  }

  private setupFloatingNavListeners(floatingNav: HTMLElement) {
    this.renderer.listen(floatingNav, "mouseleave", () => {
      this.renderer.removeChild(floatingNav.parentNode, floatingNav);
    });

    floatingNav.querySelectorAll("a").forEach((anchor) => {
      this.renderer.listen(anchor, "click", (e: Event) => {
        e.preventDefault();
        const route = anchor.getAttribute("route");
        if (route) {
          this.router.navigate([route]);
        }
      });
    });
  }

  removeFloatingNav() {
    const floatingNavs = document.querySelectorAll(".nav-floating");
    floatingNavs.forEach((nav) => {
      nav.parentNode?.removeChild(nav);
    });
  }

  isSidebarCollapsed() {
    return this.settings.getLayoutSetting("isCollapsed");
  }
  isSidebarCollapsedText() {
    return this.settings.getLayoutSetting("isCollapsedText");
  }
  isEnabledHover() {
    return this.settings.getLayoutSetting("asideHover");
  }
}
